/**
 * Planeacion: cuando preparar, cuando entregar, y que falta comprar
 * (Req. 12 y 14).
 *
 * CODIGO PURO. No conoce la base de datos.
 *
 * ESTA ES LA IDEA CENTRAL DEL PROYECTO. Un negocio de comida por encargo no
 * cocina el dia que vende: cocina antes. Mientras la aplicacion solo tenga una
 * fecha por platillo, no puede responder la pregunta de la manana, que no es
 * "que vendo hoy" sino "que tengo que cocinar hoy".
 *
 *   fecha de preparacion = fecha de venta - dias de anticipacion
 *
 * De ahi salen tres listas distintas para un mismo dia:
 *
 *   entrego hoy    -> fechaVenta == hoy   (ya deberia estar cocinado)
 *   preparo hoy    -> fechaPrep  == hoy   (se vende despues)
 *   atrasado       -> fechaPrep  < hoy y nunca se produjo
 *
 * Un platillo con anticipacion 1 que se vende el lunes aparece el domingo en
 * "preparo hoy" y el lunes en "entrego hoy". El lunes NO vuelve a aparecer en
 * "preparo hoy": esa es la confusion que este modulo existe para evitar.
 */

import { ErrorValidacion } from './errors';
import {
  sumarDias,
  esAnterior,
  type FechaLocal,
} from './dates';
import { escalarItems, type ItemEscalable } from './recipes';
import { normalizarCantidad, type FactorEquivalencia } from './units';

// ---------------------------------------------------------------------------
// Fechas de preparacion
// ---------------------------------------------------------------------------

/**
 * Dia en que hay que cocinar para llegar a la fecha de venta.
 *
 * Anticipacion 0 es valida y significa que se cocina el mismo dia que se vende
 * (Req. 12.2). Es el caso de la milanesa empanizada, que se hace al momento.
 */
export function fechaPreparacion(fechaVenta: FechaLocal, diasAnticipacion: number): FechaLocal {
  if (!Number.isInteger(diasAnticipacion) || diasAnticipacion < 0) {
    throw new ErrorValidacion(
      'Los dias de anticipacion deben ser un numero entero de cero o mas.',
      'diasAnticipacion',
    );
  }
  return sumarDias(fechaVenta, -diasAnticipacion);
}

/** Lo minimo que necesita este modulo de un platillo del menu. */
export interface ItemPlaneado {
  id: string;
  fechaVenta: FechaLocal;
  fechaPrep: FechaLocal;
  recipeId: string;
  porcionesPlaneadas: number;
  /**
   * Cuando se decidio NO cocinarlo. Null o ausente = sigue en pie.
   *
   * Existe porque un platillo que se planeo y no se vendio quedaba avisando
   * "atrasado" para siempre, sin manera de cerrarlo: la unica salida era
   * borrarlo del menu, y eso tambien borra el dato de que ese dia no hubo
   * pedidos, que es justo lo que conviene recordar al planear la semana
   * siguiente.
   *
   * Descartado NO es lo mismo que borrado: el platillo se conserva y se puede
   * volver a poner en pie si resulta que si se cocino.
   */
  descartadoEn?: string | null;
}

/** true si se decidio no cocinar este platillo. */
export function estaDescartado(item: Pick<ItemPlaneado, 'descartadoEn'>): boolean {
  return item.descartadoEn !== null && item.descartadoEn !== undefined;
}

export type ClaseAgenda =
  | 'entrega_hoy'
  | 'preparar_hoy'
  | 'atrasado'
  | 'futuro'
  | 'historico'
  | 'descartado';

export interface AgendaDelDia<T> {
  /** Se vende hoy. Deberia estar cocinado ya. */
  entregasDeHoy: T[];
  /** Hay que cocinarlo hoy, para venderlo despues. */
  prepararHoy: T[];
  /** Se debio cocinar antes y no se hizo. Visible y diferenciado (Req. 12.11). */
  atrasados: T[];
}

/**
 * Reparte los platillos en las tres listas del dia.
 *
 * `yaProducido` responde si la produccion de ese platillo ya se completo. Sin
 * ese dato, lo cocinado ayer seguiria apareciendo como pendiente para siempre.
 */
export function agendaDelDia<T extends ItemPlaneado>(
  items: readonly T[],
  hoy: FechaLocal,
  yaProducido: (item: T) => boolean,
): AgendaDelDia<T> {
  const entregasDeHoy: T[] = [];
  const prepararHoy: T[] = [];
  const atrasados: T[] = [];

  for (const item of items) {
    // Lo descartado no aparece en ninguna de las tres listas: se decidio no
    // cocinarlo, asi que no se entrega, no se prepara y no esta atrasado.
    if (estaDescartado(item)) continue;

    if (item.fechaVenta === hoy) entregasDeHoy.push(item);

    if (!yaProducido(item)) {
      if (item.fechaPrep === hoy) prepararHoy.push(item);
      else if (esAnterior(item.fechaPrep, hoy)) atrasados.push(item);
    }
  }

  const porVenta = (a: T, b: T): number => a.fechaVenta.localeCompare(b.fechaVenta);

  return {
    entregasDeHoy: entregasDeHoy.sort(porVenta),
    prepararHoy: prepararHoy.sort(porVenta),
    atrasados: atrasados.sort(porVenta),
  };
}

/**
 * Etiqueta de un platillo respecto a un dia, para mostrarla suelta.
 *
 * El orden importa: lo pendiente de cocinar gana sobre lo que se entrega hoy.
 * Un platillo que se vende hoy y nunca se preparo es el caso urgente, y decir
 * "entrega hoy" en lugar de "atrasado" ocultaria justo lo que hay que atender.
 */
export function claseDe<T extends ItemPlaneado>(
  item: T,
  hoy: FechaLocal,
  yaProducido: boolean,
): ClaseAgenda {
  // Gana sobre todo lo demas: si se decidio no cocinarlo, no esta atrasado ni
  // pendiente ni nada.
  if (estaDescartado(item)) return 'descartado';

  if (!yaProducido) {
    if (esAnterior(item.fechaPrep, hoy)) return 'atrasado';
    if (item.fechaPrep === hoy) return 'preparar_hoy';
  }
  if (item.fechaVenta === hoy) return 'entrega_hoy';
  if (esAnterior(item.fechaVenta, hoy)) return 'historico';
  return 'futuro';
}

// ---------------------------------------------------------------------------
// Lo cocinado contra lo pedido
// ---------------------------------------------------------------------------

/**
 * Que tan lejos quedo lo cocinado de lo que la gente pidio.
 *
 * POR QUE HACE FALTA
 *
 * Aqui se cocina temprano, con el estimado del menu, y los pedidos siguen
 * llegando despues. Es normal que al final no coincidan. Lo que NO puede pasar es
 * que la aplicacion se quede callada: si se cocinaron 2 chiles y nadie los pidio,
 * o si 3 personas pidieron pollo y solo se hizo 1, hay que verlo ANTES de salir a
 * repartir.
 *
 * Son tres numeros distintos y conviene no confundirlos:
 *
 *   porcionesPlaneadas -> el estimado del menu. Sirve para comprar y para
 *                         escalar la receta. No es un compromiso.
 *   porcionesAProducir -> lo que de verdad se cocino. Congela el costo.
 *   porciones (orders)  -> lo que la gente pidio. Es lo que hay que entregar.
 *
 * Esta funcion compara los dos ultimos, que son los unicos que describen hechos.
 */
export interface Desajuste {
  /** Porciones pedidas por clientes. */
  pedidas: number;
  /** Porciones cocinadas. */
  cocinadas: number;
  /**
   * Cocinadas menos pedidas.
   *
   *   negativo -> faltan por cocinar, alguien se queda sin su plato
   *   positivo -> sobran cocinadas, hay que decir a donde fueron
   *   cero     -> cuadra
   */
  diferencia: number;
  /** true cuando falta comida para cubrir los pedidos. Es el caso urgente. */
  faltan: boolean;
  /** true cuando se cocino de mas. */
  sobran: boolean;
  cuadra: boolean;
}

export function compararConPedidos(cocinadas: number, pedidas: number): Desajuste {
  const diferencia = cocinadas - pedidas;
  return {
    pedidas,
    cocinadas,
    diferencia,
    faltan: diferencia < 0,
    sobran: diferencia > 0,
    cuadra: diferencia === 0,
  };
}

// ---------------------------------------------------------------------------
// Pedidos: los lee la tabla orders, no las notas del menu
// ---------------------------------------------------------------------------

/*
 * Aqui vivia agruparPedidos(), que leia las NOTAS del menu y trataba cada nota
 * como si fuera una persona que pidio. Se escribio cuando todavia no habia
 * pedidos con nombre y lo unico disponible era texto libre.
 *
 * Se quito porque con el modulo de pedidos ya existiendo se volvio enganoso: la
 * nota descriptiva "Con ensalada y arroz." aparecia en el tablero como un pedido
 * de 4 porciones, cuando en realidad habia 2, de David y de Felipe. Un numero
 * inventado en la pantalla que se lee de reojo por la manana es peor que no
 * mostrar nada.
 *
 * Para quien es cada platillo lo responde ahora pedidosPorRecetaEntre() en
 * src/db/repos/orders.ts, con nombre y porciones reales.
 */

// ---------------------------------------------------------------------------
// Necesidades de insumos
// ---------------------------------------------------------------------------

/** Receta reducida a lo que hace falta para planear. */
export interface RecetaParaPlan {
  rendimientoPorciones: number;
  items: readonly ItemEscalable[];
}

export interface OrigenNecesidad {
  plannedItemId: string;
  recipeId: string;
  fechaVenta: FechaLocal;
  fechaPrep: FechaLocal;
  porciones: number;
  cantidadBase: number;
}

export interface NecesidadInsumo {
  ingredientId: string;
  /** Suma de todos los platillos del periodo. */
  cantidadBase: number;
  /** De donde sale, para poder explicar el renglon (Req. 14.2). */
  origenes: OrigenNecesidad[];
}

/**
 * Cuanto se necesita de cada insumo para un conjunto de platillos.
 *
 * Agrega entre platillos distintos: si el lunes lleva queso y el martes tambien,
 * el resultado es UN renglon con la suma, no dos (Req. 14.2). Es lo que evita
 * salir a comprar dos veces lo mismo.
 *
 * Los platillos cuya receta no se encuentra se omiten en silencio: pueden venir
 * de una receta borrada, y frenar el calculo entero por eso dejaria la pantalla
 * inservible.
 */
export function necesidadesPorInsumo(
  planeados: readonly ItemPlaneado[],
  recetas: ReadonlyMap<string, RecetaParaPlan>,
): NecesidadInsumo[] {
  const acumulado = new Map<string, NecesidadInsumo>();

  for (const planeado of planeados) {
    const receta = recetas.get(planeado.recipeId);
    if (receta === undefined) continue;

    const escalados = escalarItems(
      receta.items,
      planeado.porcionesPlaneadas,
      receta.rendimientoPorciones,
    );

    for (const { item, cantidadBase } of escalados) {
      if (cantidadBase <= 0) continue;

      const previo = acumulado.get(item.ingredientId);
      const origen: OrigenNecesidad = {
        plannedItemId: planeado.id,
        recipeId: planeado.recipeId,
        fechaVenta: planeado.fechaVenta,
        fechaPrep: planeado.fechaPrep,
        porciones: planeado.porcionesPlaneadas,
        cantidadBase,
      };

      if (previo === undefined) {
        acumulado.set(item.ingredientId, {
          ingredientId: item.ingredientId,
          cantidadBase,
          origenes: [origen],
        });
      } else {
        previo.cantidadBase = normalizarCantidad(previo.cantidadBase + cantidadBase);
        previo.origenes.push(origen);
      }
    }
  }

  return [...acumulado.values()];
}

// ---------------------------------------------------------------------------
// Faltantes
// ---------------------------------------------------------------------------

export interface FaltanteInsumo {
  ingredientId: string;
  necesarioBase: number;
  disponibleBase: number;
  /** max(0, necesario - disponible). Nunca negativo (Req. 14.3). */
  faltanteBase: number;
  /** true cuando el inventario alcanza y no hay que comprar. */
  alcanza: boolean;
  origenes: OrigenNecesidad[];
}

/**
 * Lo que hay que comprar: lo necesario menos lo que ya se tiene.
 *
 * El faltante nunca es negativo. Tener de sobra no genera un renglon con signo
 * contrario, simplemente no genera faltante.
 */
export function calcularFaltantes(
  necesidades: readonly NecesidadInsumo[],
  disponibleBase: (ingredientId: string) => number,
): FaltanteInsumo[] {
  return necesidades.map((necesidad) => {
    const disponible = disponibleBase(necesidad.ingredientId);
    const faltante = Math.max(0, normalizarCantidad(necesidad.cantidadBase - disponible));

    return {
      ingredientId: necesidad.ingredientId,
      necesarioBase: necesidad.cantidadBase,
      disponibleBase: disponible,
      faltanteBase: faltante,
      alcanza: faltante === 0,
      origenes: necesidad.origenes,
    };
  });
}

// ---------------------------------------------------------------------------
// Sugerencia de compra
// ---------------------------------------------------------------------------

export interface SugerenciaCompra {
  /** Cuantas unidades de compra cubren el faltante, redondeando hacia arriba. */
  cantidad: number;
  unidad: string;
  /** Cuanto queda de sobra al comprar unidades enteras. */
  sobranteBase: number;
}

/**
 * Cuantos paquetes comprar para cubrir un faltante.
 *
 * Es INFORMATIVO y no impone la cantidad (Req. 14.5, 14.10): si faltan 600 g de
 * mantequilla y cada barra trae 113.25 g, sugiere 6 barras, pero la usuaria
 * decide. Sin equivalencia declarada no hay nada que sugerir y devuelve null,
 * en lugar de inventar una conversion.
 */
export function sugerenciaDeCompra(
  faltanteBase: number,
  factorEquivalencia: FactorEquivalencia | null,
): SugerenciaCompra | null {
  if (faltanteBase <= 0) return null;
  if (factorEquivalencia === null) return null;
  if (factorEquivalencia.cantidadBase <= 0) return null;

  const cantidad = Math.ceil(faltanteBase / factorEquivalencia.cantidadBase);

  return {
    cantidad,
    unidad: factorEquivalencia.unidadCompra,
    sobranteBase: normalizarCantidad(
      cantidad * factorEquivalencia.cantidadBase - faltanteBase,
    ),
  };
}

// ---------------------------------------------------------------------------
// Periodos de la lista de compras
// ---------------------------------------------------------------------------

export type PeriodoLista = 'manana' | 'proximos' | 'semana';

/** Cuantos dias hacia adelante cubre cada periodo (Req. 14.7). */
const DIAS_POR_PERIODO: Readonly<Record<PeriodoLista, number>> = {
  manana: 1,
  proximos: 3,
  semana: 7,
};

export const ETIQUETA_PERIODO: Readonly<Record<PeriodoLista, string>> = {
  manana: 'Mañana',
  proximos: 'Próximos 3 días',
  semana: 'Toda la semana',
};

/**
 * Rango de fechas de PREPARACION que cubre un periodo.
 *
 * Se filtra por fecha de preparacion y no de venta a proposito: los insumos se
 * necesitan el dia que se cocina. Filtrar por venta pediria comprar tarde lo
 * que hay que cocinar hoy.
 */
export function rangoDelPeriodo(
  periodo: PeriodoLista,
  hoy: FechaLocal,
): { desde: FechaLocal; hasta: FechaLocal } {
  return { desde: hoy, hasta: sumarDias(hoy, DIAS_POR_PERIODO[periodo]) };
}

/** Filtra los platillos que se preparan dentro del periodo. */
export function planeadosDelPeriodo<T extends ItemPlaneado>(
  items: readonly T[],
  periodo: PeriodoLista,
  hoy: FechaLocal,
): T[] {
  const { desde, hasta } = rangoDelPeriodo(periodo, hoy);

  // Se incluye lo atrasado: si se debio cocinar ayer y no se hizo, los insumos
  // siguen haciendo falta.
  return items.filter((item) => item.fechaPrep <= hasta && item.fechaVenta >= desde);
}
